import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { type DeviceDataSnapshot, type MouseRTCapability } from '@rapoo/device-sdk'
import { toast } from '@rapoo/element'
import { useDeviceStore, useUserStore } from '@/stores'
import {
  configDelete,
  configFileUpload,
  configGet,
  configSave,
  configUpdate,
  type OnlineConfig,
} from '@/service/api/user-config'
import { defaultReportError } from '@/composables/useErrorLogger'
import type { RtPresetConfig } from '../rt/types'

export const MAX_USER_RT_PRESETS = 10

interface StoredRtPresetPayload {
  mouseRtConfig: RtPresetConfig['settings']
}

type StoredRtPreset = DeviceDataSnapshot<StoredRtPresetPayload>

function parseStoredPreset(content: string): RtPresetConfig['settings'] | null {
  try {
    const parsed = JSON.parse(content) as Partial<StoredRtPreset>
    return parsed.payload?.mouseRtConfig ?? null
  } catch {
    return null
  }
}

export function useRtCloudPresets() {
  const { t } = useI18n()
  const deviceStore = useDeviceStore()
  const userStore = useUserStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const { isLoggedIn } = storeToRefs(userStore)
  const userPresets = ref<RtPresetConfig[]>([])
  const onlineConfigs = new Map<string, OnlineConfig>()
  const isOperating = ref(false)
  const isLoadingPresets = ref(true)
  const configType = computed(
    () =>
      currentDevice.value?.manifest.capabilities.find(
        (capability): capability is MouseRTCapability => capability.type === 'mouse-rt',
      )?.config.configType ?? '',
  )

  const readFileContent = async (filePath: string): Promise<string> => {
    const originalUrl = new URL(filePath)
    const proxyUrl = `${import.meta.env.VITE_API_SOURCE_FILE}${originalUrl.pathname}`
    const response = await fetch(proxyUrl)
    if (!response.ok) throw new Error('Fetch RT preset file failed')
    return response.text()
  }

  const toPreset = async (config: OnlineConfig, index: number): Promise<RtPresetConfig | null> => {
    const content =
      config.configContent || (config.filePath ? await readFileContent(config.filePath) : '')
    const settings = parseStoredPreset(content)
    if (!settings || !config.id) return null

    onlineConfigs.set(config.id, config)
    return {
      index,
      cloudId: config.id,
      name: config.name,
      source: 'user',
      settings,
    }
  }

  const loadUserPresets = async () => {
    isLoadingPresets.value = true
    if (!isLoggedIn.value || !configType.value) {
      userPresets.value = []
      onlineConfigs.clear()
      isLoadingPresets.value = false
      return
    }

    try {
      const result = await configGet({
        configType: configType.value,
      })
      if (result.code !== 0) throw new Error(result.msg)
      // 接口按「最新 → 最旧」返回；RT 卡片按「最旧 → 最新」展示，让最新配置位于末尾。
      const configs = [...(result.data ?? [])].reverse()
      onlineConfigs.clear()
      const presets = await Promise.all(configs.map((item, index) => toPreset(item, index + 1)))
      userPresets.value = presets.filter((item): item is RtPresetConfig => item !== null)
    } catch (error) {
      userPresets.value = []
      defaultReportError(`Load mouse RT presets failed: ${JSON.stringify(error)}`)
      toast.error(t('rt.presets.loadFailed'))
    } finally {
      isLoadingPresets.value = false
    }
  }

  const uploadSnapshot = async (preset: RtPresetConfig) => {
    const device = currentDevice.value
    if (!device) throw new Error('No device available for RT preset snapshot')

    const snapshot: StoredRtPreset = {
      metadata: {
        deviceType: device.manifest.type,
        deviceModel: device.manifest.model,
        timestamp: Date.now(),
        sdkVersion: device.sdkVersion,
      },
      payload: {
        mouseRtConfig: preset.settings,
      },
    }
    const content = JSON.stringify(snapshot)
    const file = new File([content], 'mouse-rt-preset.json', { type: 'application/json' })
    const result = await configFileUpload(file)
    if (result.code !== 0 || !result.data.filePath) {
      throw new Error(result.msg || 'Upload RT preset file failed')
    }
    return result.data.filePath
  }

  /**
   * save 接口成功时可能不返回 data；此时静默查询服务端列表，
   * 只通过本次上传文件的唯一路径找回新记录，不触发列表 loading 或整体重载。
   */
  const resolveSavedConfig = async (
    configToSave: OnlineConfig,
    responseConfig: OnlineConfig | null,
  ) => {
    if (responseConfig?.id) return { ...configToSave, ...responseConfig }

    const result = await configGet({ configType: configToSave.configType })
    if (result.code !== 0) throw new Error(result.msg)

    const savedConfig = result.data?.find(
      (config) => config.id && config.filePath === configToSave.filePath,
    )
    if (!savedConfig?.id) throw new Error('Unable to resolve saved config ID')
    return savedConfig
  }

  const createUserPreset = async (name: string, preset: RtPresetConfig) => {
    if (userPresets.value.length >= MAX_USER_RT_PRESETS) {
      toast.warning(t('rt.presets.maxReached', { count: MAX_USER_RT_PRESETS }))
      return null
    }

    isOperating.value = true
    try {
      const filePath = await uploadSnapshot(preset)
      const configToSave: OnlineConfig = {
        name,
        filePath,
        configContent: '',
        configType: configType.value,
      }
      const result = await configSave(configToSave)
      if (result.code !== 0) throw new Error(result.msg)

      const savedConfig = await resolveSavedConfig(configToSave, result.data)
      const cloudId = savedConfig.id!
      const createdPreset: RtPresetConfig = {
        index: userPresets.value.length + 1,
        cloudId,
        name: savedConfig.name,
        source: 'user',
        settings: preset.settings,
      }
      onlineConfigs.set(cloudId, savedConfig)
      userPresets.value = [...userPresets.value, createdPreset]

      toast.success(t('rt.presets.uploaded'))
      return createdPreset
    } catch (error) {
      defaultReportError(`Create mouse RT preset failed: ${JSON.stringify(error)}`)
      toast.error(t('rt.presets.uploadFailed'))
      return null
    } finally {
      isOperating.value = false
    }
  }

  const overwriteUserPreset = async (cloudId: string, preset: RtPresetConfig) => {
    const current = onlineConfigs.get(cloudId)
    if (!current) return false

    isOperating.value = true
    try {
      const filePath = await uploadSnapshot(preset)
      const configToUpdate: OnlineConfig = {
        ...current,
        filePath,
        configContent: '',
        configType: configType.value,
      }
      const result = await configUpdate(configToUpdate)
      if (result.code !== 0) throw new Error(result.msg)

      const updatedConfig = { ...configToUpdate, ...(result.data ?? {}), id: cloudId }
      onlineConfigs.set(cloudId, updatedConfig)
      userPresets.value = userPresets.value.map((item) =>
        item.cloudId === cloudId
          ? { ...item, name: updatedConfig.name, settings: preset.settings }
          : item,
      )

      toast.success(t('rt.presets.overwritten'))
      return true
    } catch (error) {
      defaultReportError(`Overwrite mouse RT preset failed: ${JSON.stringify(error)}`)
      toast.error(t('rt.presets.overwriteFailed'))
      return false
    } finally {
      isOperating.value = false
    }
  }

  const renameUserPreset = async (cloudId: string, newName: string) => {
    const current = onlineConfigs.get(cloudId)
    const trimmedNewName = newName.trim()
    if (!current || !trimmedNewName) return false

    isOperating.value = true
    try {
      const result = await configUpdate({
        ...current,
        name: trimmedNewName,
      })
      if (result.code !== 0) throw new Error(result.msg)

      onlineConfigs.set(cloudId, { ...current, name: trimmedNewName })
      userPresets.value = userPresets.value.map((preset) =>
        preset.cloudId === cloudId ? { ...preset, name: trimmedNewName } : preset,
      )

      toast.success(t('rt.presets.edited'))
      return true
    } catch (error) {
      defaultReportError(`Rename mouse RT preset failed: ${JSON.stringify(error)}`)
      toast.error(t('rt.presets.editFailed'))
      return false
    } finally {
      isOperating.value = false
    }
  }

  const deleteUserPreset = async (cloudId: string) => {
    isOperating.value = true
    try {
      const result = await configDelete(cloudId)
      if (result.code !== 0) throw new Error(result.msg)

      onlineConfigs.delete(cloudId)
      userPresets.value = userPresets.value
        .filter((preset) => preset.cloudId !== cloudId)
        .map((preset, index) => ({ ...preset, index: index + 1 }))

      toast.success(t('rt.presets.deleted'))
      return true
    } catch (error) {
      defaultReportError(`Delete mouse RT preset failed: ${JSON.stringify(error)}`)
      toast.error(t('rt.presets.deleteFailed'))
      return false
    } finally {
      isOperating.value = false
    }
  }

  return {
    isLoggedIn,
    configType,
    userPresets,
    isOperating,
    isLoadingPresets,
    loadUserPresets,
    createUserPreset,
    overwriteUserPreset,
    deleteUserPreset,
    renameUserPreset,
  }
}
